<template>
    <div class="lp-wave-content">
      <div class="lp-wave-login">
        <div class="ly-row login-container ly-center-center" >
          <div :class="['login-content', 'ly-col', 'ly-center-center']">
            <div class="login-content-form" v-load-fade-in="{ delay: 1000, timeBetween: 500 }">
              <transition name="fade">
                <div v-show="true" class="lp-load-fadein">
                  <div v-if="oidcSessionLoading" class="oidc-loading-container">
                    <div class="oidc-spinner"></div>
                    <p class="oidc-loading-text">Checking session...</p>
                  </div>

                  <template v-else>
                    <div>
                      <h1 class="text-center text-black-100 font-weight-bold">Sign In</h1>
                    </div>

                    <NewLoginForm v-if="showLoginForm" @formSubmitted="onFormSubmitted" :isSubmitting="submitting" />

                    <div class="mt-3" v-if="!showLoginForm && (showSamlBtn || showGoogleBtn)"/>

                    <div class="msg msg-error w-100 text-center" v-if="errorMessage">
                      {{ errorMessage }}
                    </div>
                    <div v-if="showStrokedTextContainer" class="stroked-text-container">
                      <div class="stroked-text"></div>
                      <span>or</span>
                    </div>

                    <GoogleLoginButton class="mt-1" v-if="showGoogleBtn" @googleLogin="onGoogleLogin" />

                    <div v-if="showSamlBtn && showGoogleBtn" class="mt-1" />
                    <SamlLoginButton v-if="showSamlBtn" v-on:click="onSamlLogin" :isSubmitting="submittingIdp" />

                    <div class="mt-3 text-center">
                      <p class="text-size-normal">
                        Don't have an account?
                        <a @click="onClickGetStarted" class="blue-link">Request a Demo</a>
                      </p>
                    </div>
                  </template>
                </div>
              </transition>
            </div>
          </div>
        </div>
      </div>
    </div>
</template>

<script setup lang="ts">
import NewLoginForm from './components/NewLoginForm.vue'
import GoogleLoginButton from './components/GoogleLoginButton.vue'
import { inject, onMounted, ref, Ref, computed, onBeforeMount } from 'vue'
import { ServiceNames } from '@/types/serviceNames'
import { LMS, CONNECTIONS, SECURE_PAYMENTS } from '@/consts/serviceNames'
import SamlLoginButton from './components/SamlLoginButton.vue'
import { useConfig, useConfigSync } from '@/config/useConfig'
import { Redirect } from '@/types/redirect'
import { LoginUIMethods } from '@/types/interfaces'
import { getOidcParams, buildOidcAuthorizeUrl, OidcParams } from '@/http/auth'
import { getQueryParameter } from '@/services/getQueryParameter'

const submitting: Ref<boolean> = ref(false)
const submittingIdp: Ref<boolean> = ref(false)
const errorMessage: Ref<string> = ref('')
const { shouldPassOidcParams: hasOidcParams } = getOidcParams()
const promptParam = getQueryParameter('prompt')
const oidcSessionLoading: Ref<boolean> = ref(
  Boolean(hasOidcParams) && promptParam !== 'login'
)
const serviceSelector: Ref<ServiceNames> = ref(LMS)
const loginUiMethods: Ref<LoginUIMethods> = ref({
  username_password_login: false,
  google_login: false,
  forgot_password: false
})
const showFlags: Ref<{ secure_payments_btn: boolean, connections_btn: boolean }> = ref({
  secure_payments_btn: true,
  connections_btn: true
})

const usernamePasswordLogin = inject('usernamePasswordLogin') as (username: string, password: string, selectedService: ServiceNames) => Promise<void>
const googleLogin = inject('googleLogin') as (googleToken: string, redirectUrlAfterLogin: string) => Promise<void>
const samlLogin = inject('samlLogin') as (customProviderUrl?: string) => Promise<{ idp_url: string }>
const redirect = inject('redirect') as Redirect

const loadUIMethods = async () => {
  const config = await useConfig()
  loginUiMethods.value = config.login_ui_methods
  showFlags.value = {
    secure_payments_btn: !config.hide_flags?.secure_payments_btn,
    connections_btn: !config.hide_flags?.connections_btn
  }
}

const showStrokedTextContainer = computed(() => {
  return showLoginForm.value && (showGoogleBtn.value || showSamlBtn.value)
})

const showGoogleBtn = computed(() => {
  const isActive = loginUiMethods.value.google_login === true || loginUiMethods.value.google_login === 'true'
  return isActive && serviceSelector.value === 'LMS'
})

const showSamlBtn = computed(() => {
  const config = useConfigSync()
  const isActive = config?.saml?.loginActive === true || config?.saml?.loginActive === 'true'
  return isActive && serviceSelector.value === 'LMS'
})

const showLoginForm = computed(() => {
  return loginUiMethods.value.username_password_login === true || loginUiMethods.value.username_password_login === 'true' ||
    serviceSelector.value !== 'LMS'
})

const triggerSamlLogin = async () => {
  const config = await useConfig()
  const host = window.location.hostname
  const samlHostname = config.saml.samlURL.replace(/(http:\/\/)|(https:\/\/)+/, '')

  // If it does. The SAML payload and SAML url are comming from LMS.
  if (config.saml.samlURL.includes('UserIdentityProvider.samlRequest')) {
    await samlLogin(config.saml.samlURL)
    return
  }

  if (config.saml.samlURL && !samlHostname.includes(host)) {
    window.location.href = config.saml.samlURL
    return
  }

  await samlLogin()
}

const onFormSubmitted = async (params: { email: string, password: string }) => {
  if (submitting.value) return
  submitting.value = true
  errorMessage.value = ''
  try {
    await usernamePasswordLogin(params.email, params.password, serviceSelector.value)
  } catch (error: any) {
    if (error.code === 'PasswordExpired') {
      redirect('/reset')
    }
    displayError(error)
  }
  submitting.value = false
}

const onGoogleLogin = async (params: { googleToken: string }) => {
  if (submitting.value) return
  submitting.value = true
  try {
    await googleLogin(params.googleToken, serviceSelector.value)
  } catch (error: any) {
    displayError(error)
  }
  submitting.value = false
}

const onSamlLogin = async () => {
  if (submitting.value) return
  submittingIdp.value = true
  try {
    triggerSamlLogin()
  } catch (error: any) {
    displayError(error)
  }
  submitting.value = false
}

const setProvider = (provider: string) => {
  window.localStorage.setItem('provider', provider)
}

const displayError = (error: any) => {
  error.message
    ? errorMessage.value = error.message
    : errorMessage.value = 'Unexpected Error'
}

const onClickLearnMore = async () => {
  const config = await useConfig()
  window.location.href = config.sso.learn_more_hyperlink
}

const onClickGetStarted = async () => {
  const config = await useConfig()
  let getStartedLink = config.sso.get_started_hyperlink
  switch (serviceSelector.value) {
    case CONNECTIONS:
      getStartedLink = config.connections.get_started_hyperlink
      break
    case SECURE_PAYMENTS:
      getStartedLink = config.sp.get_started_hyperlink
      break
  }
  redirect(getStartedLink, true, true)
}
const onClickForgotPassword = async () => {
  const config = await useConfig()
  switch (serviceSelector.value) {
    case CONNECTIONS:
      redirect(config.connections.forgot_password_hyperlink, true, true)
      break
    case SECURE_PAYMENTS:
      redirect(config.sp.forgot_password_hyperlink, true, true)
      break
    default:
      redirect('ForgotView')
  }
}

const checkOidcSessionRedirect = async (): Promise<boolean> => {
  const { shouldPassOidcParams, oidcParams } = getOidcParams()
  if (!shouldPassOidcParams) {
    oidcSessionLoading.value = false
    return false
  }

  // If backend already rejected the session and redirected back with prompt=login, don't retry (avoid infinite loop)
  const prompt = getQueryParameter('prompt')
  if (prompt === 'login') {
    oidcSessionLoading.value = false
    return false
  }

  const config = await useConfig()
  const apiPath = `${config.api.url}${config.api.service_path}`
  const redirectUrl = buildOidcAuthorizeUrl(apiPath, oidcParams as OidcParams)
  window.location.href = redirectUrl
  return true
}

onMounted(async () => {
  setProvider(serviceSelector.value)
  await checkOidcSessionRedirect()
})

onBeforeMount(async () => {
  await loadUIMethods()
})

</script>

<style scoped>

.login-container {
  height: 100%;
  background: transparent;
}

h1 {
  font-size: 36px!important;
}

.sidebar {
  background: #27313e;
  height: 100%;
  padding-top: 30px;
  background-image: url(@/assets/img/login-circles-bg.png);
  background-repeat: no-repeat;
  background-size: 100%;
  background-position: bottom left;
}

.login-info .font-weight-bold {
  font-size: 18px;
  line-height: 24px;
}

.login-info {
  margin-top: 180px;
  color: #fff;
}

p,
li {
  font-size: 15px;
}

.sidebar li {
  padding-left: 1.5em;
  position: relative;
  margin-bottom: 1rem;
}

.sidebar li:before {
  content: "";
  position: absolute;
  left: 0;
  top: 4px;
  width: 1rem;
  height: 1rem;
  background-repeat: no-repeat;
  background-image: url(@/assets/img/orange-checkmark.svg);
}

.lp .sidebar .btn {
  background: transparent;
  color: #fff;
  border-color: #fff;
  transition: all 0.3s;
}

.sidebar .btn:hover {
  background: #fff;
  color: #27313e;
  border-color: #fff;
  transition: all 0.3s;
}

.login-content-form {
  width: 400px;
  margin: 0 auto;
}

.stroked-text-container {
  position: relative;
  text-align: center;
  margin-top: 10px;
  margin-bottom: 20px;
}

.stroked-text {
  position: absolute;
  border-top: 1px solid #d3dae4;
  top: 10px;
  width: 100%;
  z-index: 9;
}

.stroked-text-container span {
  background: #fff;
  z-index: 10;
  display: inline-block;
  position: relative;
  padding: 0 10px;
}

.text-size-normal {
  font-size: 1rem;
}

.service-selector .lp-radio-wrapper {
  position: relative;
  display: block;
}

.service-selector .lp-radio-wrapper:before {
  position: relative;
  content: "";
  height: 80px;
  border: 1px solid #FFF;
  display: flex;
  justify-content: center;
  align-items: center;
  background-size: 28px 28px;
  background-position: center 10px;
  background-repeat: no-repeat;
  border-radius: 10px;
  /* box-shadow: 1px 1px 6px rgba(94, 118, 151, 0.12); */
  box-shadow: 0px 4px 16.1px 0px #0000000D;

}

.service-selector .lp-radio-wrapper:nth-child(1):before {
  background-image: url(@/assets/img/lms-login-logo.png);
}

.service-selector .lp-radio-wrapper:nth-child(2):before {
  background-image: url(@/assets/img/sp-login-logo.png);
}

.service-selector .lp-radio-wrapper:nth-child(3):before {
  background-image: url(@/assets/img/connections-login-logo.png);
}

.service-selector .lp-radio-wrapper:after {
  position: relative;
  display: block;
  text-align: center;
  margin-top: -28px;
  font-size: 12px;
  line-height: 14px;
  color: var(--black-100);
}

.service-selector .lp-radio-wrapper:nth-child(1):after {
  content: "LMS Center";
  white-space: pre;
}

.service-selector .lp-radio-wrapper:nth-child(2):after {
  content: "Adaptive Waller";
}

.service-selector .lp-radio-wrapper:nth-child(3):after {
  content: "Connections";
}

.service-selector .lp-radio-wrapper-primary:before {
  /* border: 1px solid rgba(94, 118, 151, 0.25); */
  /* box-shadow: 1px 1px 6px rgba(94, 118, 151, 0.12), inset 0 0 0px 2px #FFF; */
  /* box-shadow: 0px 4px 16.1px 0px #0000000D; */
  transition: all .3s;
  background-color: #FFF;
}

.service-selector .lp-radio-wrapper-primary:hover:before {
  box-shadow: 0px 4px 16.1px 0px #3022BB33;
  transition: all .3s;
}

.service-selector .lp-radio-wrapper-primary-checked:before {
  border: 1px solid var(--primary);
  /* box-shadow: 0 4px 10px rgba(94, 118, 151, 0.22), inset 0 0 0px 2px #1a62ff; */
  /* box-shadow: 0px 4px 16.1px 0px #3022BB33; */
}

.service-selector .lp-radio-wrapper-primary-checked:after {
  color: var(--black);
  font-weight: bold;
}

.login-logo {
  width: 210px;
}

.login-logo img {
  width: 100%;
  height: auto;
}

.pismo-login-logo,
.galileo-login-logo {
  width: 200px;
  text-align: center;
  margin: 0 auto;
}

.pismo-login-logo img,
.galileo-login-logo img {
  width: 100%;
  height: auto;
}

.blue-link {
  color: #348BFF;
}

.oidc-loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 64px 0;
  margin-top: 128px
}

.oidc-spinner {
  width: 36px;
  height: 36px;
  border: 3px solid #e2e8f0;
  border-top-color: #348BFF;
  border-radius: 50%;
  animation: oidc-spin 0.8s linear infinite;
}

.oidc-loading-text {
  margin-top: 16px;
  font-size: 1rem;
  color: #64748b;
}

@keyframes oidc-spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
